import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Button,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { TextField, PasswordField, SelectField } from "components";
import { NotificationManager } from "react-notifications";
import { useForm } from "react-hook-form";
import { useYup, useFetch } from "hooks";
import { endpoints } from "appConfigs";
import { getUserLocation } from "helpers";
import * as yup from "yup";
import { FormattedMessage } from "react-intl";

const addSchema = yup.object({
  hospital_id: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),

  name: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  phone: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  email: yup
    .string()
    .email(
      <FormattedMessage
        id="validationEmail"
        defaultMessage="Enter a valid email"
      />
    )
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),

  pin_number: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  confirm_pin_number: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    )
    .oneOf(
      [yup.ref("pin_number")],
      <FormattedMessage
        id="validationPinConfirm"
        defaultMessage="Pin number did not match"
      />
    ),

  password: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  confirm_password: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    )
    .oneOf(
      [yup.ref("password")],
      <FormattedMessage
        id="validationConfirmPassword"
        defaultMessage="Password did not match"
      />
    ),

  height: yup
    .number()
    .min(
      10,
      <FormattedMessage
        id="validationMinNum"
        defaultMessage="Can't be less than {num}"
        values={{ num: 10 }}
      />
    )
    .transform((v) => +v || null)
    .nullable(),
  weight: yup
    .number()
    .min(
      0.25,
      <FormattedMessage
        id="validationMinNum"
        defaultMessage="Can't be less than {num}"
        values={{ num: 2.5 }}
      />
    )
    .transform((v) => +v || null)
    .nullable(),
  blood_group: yup.string().nullable(),
  status: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
});
const updateSchema = yup.object({
  hospital_id: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),

  name: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  phone: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
  email: yup
    .string()
    .email(
      <FormattedMessage
        id="validationEmail"
        defaultMessage="Enter a valid email"
      />
    )
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),

  pin_number: yup.string(),
  confirm_pin_number: yup
    .string()
    .oneOf(
      [yup.ref("pin_number")],
      <FormattedMessage
        id="validationPinConfirm"
        defaultMessage="Pin number did not match"
      />
    ),

  password: yup.string(),
  confirm_password: yup
    .string()
    .oneOf(
      [yup.ref("password")],
      <FormattedMessage
        id="validationConfirmPassword"
        defaultMessage="Password did not match"
      />
    ),

  height: yup
    .number()
    .min(
      10,
      <FormattedMessage
        id="validationMinNum"
        defaultMessage="Can't be less than {num}"
        values={{ num: 10 }}
      />
    )
    .transform((v) => +v || null)
    .nullable(),
  weight: yup
    .number()
    .min(
      0.25,
      <FormattedMessage
        id="validationMinNum"
        defaultMessage="Can't be less than {num}"
        values={{ num: 2.5 }}
      />
    )
    .transform((v) => +v || null)
    .nullable(),
  blood_group: yup.string().nullable(),
  status: yup
    .string()
    .required(
      <FormattedMessage
        id="fieldIsRequired"
        defaultMessage="Field is required"
      />
    ),
});

const useStyles = makeStyles((theme) => ({
  btns: {
    margin: "0 auto",
    marginBottom: "1rem",
    display: "flex",
    gridGap: "1rem",
    justifyContent: "flex-end",
  },
}));

const Form = ({ open, edit, authUser, setEdit, onSuccess }) => {
  const s = useStyles();
  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ resolver: useYup(edit?.id ? updateSchema : addSchema) });
  const {
    post: addUser,
    put: updateUser,
    loading,
  } = useFetch(endpoints.users + `/${edit?.id || ""}`);

  const hospital_id = watch("hospital_id");

  useEffect(() => {
    const newPass = Math.random().toString(36).substr(2, 8);
    const newPin = Math.random().toString().substr(2, 4);
    reset({
      ...edit,
      ...(!edit?.id && {
        password: newPass,
        confirm_password: newPass,
        pin_number: newPin,
        confirm_pin_number: newPin,
      }),
      hospital_id:
        authUser.user_type === "hospital"
          ? authUser.id
          : edit?.hospital?.id || "",
    });
  }, [edit]);
  return (
    <Dialog open={open}>
      <DialogTitle>
        {edit?.id ? (
          <FormattedMessage id="editUser" defaultMessage="Edit User" />
        ) : (
          <FormattedMessage id="addUser" defaultMessage="Add User" />
        )}
      </DialogTitle>
      <DialogContent>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(async (values) => {
            const { position, error } = await getUserLocation();
            if (error) {
              return NotificationManager.error(error);
            }
            (edit?.id ? updateUser : addUser)({
              ...values,
              password: values.password || undefined,
              pin_number: values.pin_number || undefined,
              age: undefined,
              latitude: position?.latitude,
              longitude: position?.longitude,
            })
              .then(({ data }) => {
                if (data.success) {
                  NotificationManager.success(data.message);
                  onSuccess(data.data);
                } else {
                  NotificationManager.error(data.message);
                }
              })
              .catch((err) => {
                NotificationManager.error(err.message);
              });
          })}
        >
          <Grid container spacing={3}>
            <Grid item sm={12}>
              <TextField
                fullWidth
                {...register("name")}
                label={<FormattedMessage id="name" defaultMessage="Name" />}
                error={errors.name}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                {...register("phone")}
                label={<FormattedMessage id="phone" defaultMessage="Phone" />}
                error={errors.phone}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                {...register("email")}
                label={<FormattedMessage id="email" defaultMessage="Email" />}
                error={errors.email}
              />
            </Grid>

            {authUser.user_type === "admin" && (
              <Grid item sm={12}>
                <SelectField
                  label={
                    <FormattedMessage id="hospital" defaultMessage="Hospital" />
                  }
                  name="hospital_id"
                  control={control}
                  url={endpoints.hospitals}
                  getQuery={(inputValue, selected) => ({
                    ...(inputValue && { name: inputValue }),
                    ...(selected && { id: selected }),
                  })}
                  handleData={(data) => ({
                    label: data.name,
                    value: data.id,
                  })}
                />
              </Grid>
            )}

            {
              // !edit?.id && (
              <>
                <Grid item sm={6}>
                  <PasswordField
                    fullWidth
                    {...register("password")}
                    label={
                      <FormattedMessage
                        id="password"
                        defaultMessage="Password"
                      />
                    }
                    error={errors.password}
                  />
                </Grid>

                <Grid item sm={6}>
                  <PasswordField
                    fullWidth
                    {...register("confirm_password")}
                    label={
                      <FormattedMessage
                        id="confirmPassword"
                        defaultMessage="Confirm Password"
                      />
                    }
                    error={errors.confirm_password}
                  />
                </Grid>
              </>
              // )
            }

            <Grid item sm={6}>
              <PasswordField
                fullWidth
                {...register("pin_number")}
                label={
                  <FormattedMessage
                    id="pinNumber"
                    defaultMessage="Pin Number"
                  />
                }
                error={errors.pin_number}
              />
            </Grid>

            <Grid item sm={6}>
              <PasswordField
                fullWidth
                {...register("confirm_pin_number")}
                label={
                  <FormattedMessage
                    id="confirmPinNumber"
                    defaultMessage="Confirm Pin Number"
                  />
                }
                error={errors.confirm_pin_number}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                type="date"
                fullWidth
                {...register("dob")}
                label={
                  <FormattedMessage
                    id="dateOfBirth"
                    defaultMessage="Date of Birth"
                  />
                }
                error={errors.dob}
              />
            </Grid>

            <Grid item sm={6}>
              <TextField
                fullWidth
                type="number"
                {...register("height")}
                label={<FormattedMessage id="height" defaultMessage="Height" />}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">Inch</InputAdornment>
                  ),
                }}
                error={errors.height}
              />
            </Grid>

            <Grid item sm={6}>
              <TextField
                fullWidth
                type="number"
                {...register("weight")}
                label={<FormattedMessage id="weight" defaultMessage="Weight" />}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">KG</InputAdornment>
                  ),
                }}
                error={errors.weight}
              />
            </Grid>

            <Grid item sm={12}>
              <SelectField
                label={
                  <FormattedMessage
                    id="bloodGroup"
                    defaultMessage="Blood Group"
                  />
                }
                name="blood_group"
                control={control}
                options={[
                  { label: "A+", value: "A+" },
                  { label: "A-", value: "A-" },
                  { label: "B+", value: "B+" },
                  { label: "B-", value: "B-" },
                  { label: "O+", value: "O+" },
                  { label: "O-", value: "O-" },
                  { label: "AB+", value: "AB+" },
                  { label: "AB-", value: "AB-" },
                ]}
              />
            </Grid>

            <Grid item sm={12}>
              <SelectField
                label={<FormattedMessage id="status" defaultMessage="Status" />}
                name="status"
                control={control}
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                {...register("hospital_number")}
                label={
                  <FormattedMessage
                    id="hospitalNumber"
                    defaultMessage="Hospital Number"
                  />
                }
                error={errors.hospital_number}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                {...register("free_appointments")}
                label={
                  <FormattedMessage
                    id="freeAppointments"
                    defaultMessage="Free Appointments"
                  />
                }
                error={errors.free_appointments}
              />
            </Grid>

            <Grid item sm={12} className={s.btns}>
              <Button
                type="button"
                variant="contained"
                disabled={loading}
                onClick={() => setEdit(null)}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                type="submit"
                disabled={loading}
                variant="contained"
                color="primary"
              >
                <FormattedMessage id="submit" defaultMessage="Submit" />
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Form;
